import { WorldMap } from "components/organisms";
import { Bubbles } from "components/molecules";
import { BackButton, Card, Content, Page, TopBar } from "components/templates";
import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import { learnHref, lensHref } from "utils/learnPaths";
import countriesData from "utils/countriesData.json";
import { applyLens, findLearnLens, LEARN_LENSES } from "utils/learnLenses";
import { AvailableLanguages } from "utils/types";

const PageTitle = styled.h1`
  margin: 0 0 6px;
  font-family: "Gaegu", cursive;
  font-size: clamp(2rem, 5vw, 2.6rem);
  line-height: 1.05;
  color: #f5e2c8;
  text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35);
`;

const Summary = styled.p`
  margin: 0 0 16px;
  max-width: 100%;
  font-family: "Gaegu", cursive;
  font-size: 1.3rem;
  line-height: 1.4;
  color: rgba(245, 226, 200, 0.92);
`;

const MapPanel = styled.div`
  position: relative;
  height: min(56vh, 460px);
  min-height: 260px;
  border-radius: 18px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(27, 38, 79, 0.25);
  overflow: hidden;
`;

const Legend = styled.ul`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LegendItem = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid rgba(245, 226, 200, 0.2);
  background: rgba(24, 40, 72, 0.4);
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  color: rgba(245, 226, 200, 0.95);
`;

const Swatch = styled.span<{ $color: string }>`
  width: 13px;
  height: 13px;
  border-radius: 4px;
  flex: 0 0 auto;
  background: ${({ $color }) => $color};
  border: 1px solid rgba(0, 0, 0, 0.3);
`;

const Count = styled.span`
  font-family: "Finger Paint", cursive;
  font-size: 0.72rem;
  color: rgba(245, 226, 200, 0.65);
`;

const BodyCard = styled(Card)`
  margin-top: 18px;
`;

const Paragraph = styled.p`
  margin: 0 0 10px;
  font-family: "Gaegu", cursive;
  font-size: 1.22rem;
  line-height: 1.45;
  color: rgba(245, 226, 200, 0.95);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SourceNote = styled.p`
  margin: 14px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.6);
`;

const OtherLenses = styled.div`
  margin-top: 26px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const LensLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.2);
  background: rgba(27, 38, 79, 0.28);
  color: #f5e2c8;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  text-decoration: none;

  &:hover {
    border-color: rgba(173, 216, 230, 0.6);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const ui = {
  en: {
    back: "Back",
    backAria: "Go back",
    notFound: "We couldn't find that lens.",
    allTopics: "All of Learn",
    noData: "No data",
    otherLenses: "Other lenses",
    countries: (n: number) => `${n}`,
  },
  es: {
    back: "Volver",
    backAria: "Volver",
    notFound: "No encontramos esa lente.",
    allTopics: "Todo Aprender",
    noData: "Sin datos",
    otherLenses: "Otras lentes",
    countries: (n: number) => `${n}`,
  },
};

export const LearnLens = () => {
  const { lensSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useAppLanguage();
  const isSpanish = language === AvailableLanguages.ES;
  const t = isSpanish ? ui.es : ui.en;

  const lens = findLearnLens(lensSlug);
  const result = useMemo(() => (lens ? applyLens(lens) : undefined), [lens]);

  if (!lens || !result) {
    return (
      <Page id="learn-lens" lang={isSpanish ? "es" : "en"}>
        <Bubbles />
        <Content>
          <TopBar>
            <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
              <span aria-hidden="true">←</span> {t.back}
            </BackButton>
          </TopBar>
          <Card>
            <Paragraph>{t.notFound}</Paragraph>
            <LensLink to={learnHref(isSpanish)}>{t.allTopics}</LensLink>
          </Card>
        </Content>
      </Page>
    );
  }

  const locale = isSpanish ? lens.es : lens.en;

  return (
    <Page id="learn-lens" lang={isSpanish ? "es" : "en"}>
      <Bubbles />
      <Content>
        <TopBar>
          <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
            <span aria-hidden="true">←</span> {t.back}
          </BackButton>
        </TopBar>

        <PageTitle>
          <span aria-hidden="true">{lens.emoji}</span> {locale.title}
        </PageTitle>
        <Summary>{locale.summary}</Summary>

        <MapPanel>
          <WorldMap
            data={countriesData}
            language={language ?? AvailableLanguages.EN}
            showControls={false}
            fillHeight
            countryColors={result.colors}
            showHoverInvite={false}
          />
        </MapPanel>

        <Legend>
          {lens.categories.map((category) => {
            const count = result.counts[category.id] ?? 0;
            if (count === 0) return null;
            return (
              <LegendItem key={category.id}>
                <Swatch $color={category.color} aria-hidden="true" />
                {isSpanish ? category.es : category.en}
                <Count>{t.countries(count)}</Count>
              </LegendItem>
            );
          })}
          {result.noData > 0 && (
            <LegendItem>
              <Swatch $color="#2980b9" aria-hidden="true" />
              {t.noData}
              <Count>{t.countries(result.noData)}</Count>
            </LegendItem>
          )}
        </Legend>

        <BodyCard>
          {locale.body.map((paragraph) => (
            <Paragraph key={paragraph}>{paragraph}</Paragraph>
          ))}
          <SourceNote>{locale.sourceNote}</SourceNote>
        </BodyCard>

        <OtherLenses>
          {/* Lenses read best in sequence — the same map, re-coloured. */}
          {LEARN_LENSES.filter((other) => other.slug !== lens.slug).map(
            (other) => (
              <LensLink key={other.slug} to={lensHref(isSpanish, other)}>
                <span aria-hidden="true">{other.emoji}</span>
                {isSpanish ? other.es.title : other.en.title}
              </LensLink>
            )
          )}
        </OtherLenses>
      </Content>
    </Page>
  );
};
